import { useId, useState } from 'react';
import { MagnifyingGlassIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import type { tMessageSourcesProps } from './MessageSources.types';
import { sourceFileKind } from './MessageSources.functions';
import './MessageSources.styles.scss';

const NS = 'common/messageSources';

/**
 * AGI-243 "Ver fuentes" / AGI-272 "chip de evidencia": a collapsed-by-default
 * control that lists what the Lead Agent grounded a reply on. Mounted inside a
 * Lead Agent bot bubble in two independent trees — the live chat
 * (MessageBubbleV2) and the sandbox (BotSandbox) — so it stays a standalone
 * component instead of an extension of either bubble. Internal to the
 * operator; the lead never sees it. Renders nothing (not even the chip) when
 * there are no sources.
 *
 * AGI-272: the toggle is a plain `<button aria-expanded>`, never daisyUI's
 * `Collapse`. Collapse builds the control out of a hidden `<input
 * type="checkbox">` and a grid row, and that input keeps daisyUI's
 * `min-height: 3.75rem` + `padding: 1rem` no matter what the container's own
 * SCSS says — 60 px of box for a 15 px label, full bubble width, an arrow
 * pinned to the far edge, and a focus ring drawn around the whole empty
 * rectangle. Every one of those defects is Collapse geometry, so the fix is
 * to stop using Collapse.
 */
export default function MessageSources(props: tMessageSourcesProps) {
  const { sources } = props;
  const { t } = useTranslation(NS);
  const [isOpen, setIsOpen] = useState(false);
  const cardId = useId();

  if (!sources || sources.length === 0) return null;

  return (
    <div className="MessageSources">
      {/* The count answers the operator's actual question while closed — "where
          did this come from?" → "from three things" — so the old "Ver fuentes"
          label and the intro line above the list are both gone. */}
      <button
        type="button"
        className="MessageSources__Chip"
        aria-expanded={isOpen}
        aria-controls={cardId}
        onClick={() => setIsOpen((open) => !open)}
      >
        <MagnifyingGlassIcon className="MessageSources__ChipIcon" aria-hidden="true" />
        <span>{t('chip', { count: sources.length })}</span>
      </button>

      {/* Kept mounted and hidden rather than unmounted, the way the Collapse it
          replaces behaved: `hidden` is what aria-expanded promises, it keeps
          assistive tech and the toggle in agreement, and it avoids re-creating
          the rows on every open. */}
      <ul className="MessageSources__Card" id={cardId} hidden={!isOpen}>
        {sources.map((source, index) => {
          if (source.kind === 'file') {
            const kind = sourceFileKind(source.name);
            return (
              // Keyed by index, not by id: two sources of the same kind can
              // carry the same id (one file can ground a reply twice), and a
              // duplicate React key silently drops the second row.
              <li
                key={`file-${index}`}
                className="MessageSources__Item"
                data-testid="message-source-file"
              >
                <DocumentTextIcon className="MessageSources__FileIcon" aria-hidden="true" />
                {/* The full name always reaches the DOM and the `title`; the
                    single-line clipping is CSS, so nothing is lost for copy,
                    search or a screen reader. */}
                <span className="MessageSources__FileName" title={source.name}>
                  {source.name}
                </span>
                {kind && <span className="MessageSources__FileKind">{kind}</span>}
              </li>
            );
          }

          return (
            <li
              key={`excerpt-${index}`}
              className="MessageSources__Item"
              data-testid="message-source-excerpt"
            >
              <span className="MessageSources__Excerpt">
                &ldquo;{source.excerpt}&rdquo;
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
